import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import {
  BeneficiaryDocument,
  useBeneficiaryQuery,
  useDeleteBeneficiaryMutation,
} from "@common/utils/graphql";
import { FullPageLoader } from "@common/components/Loading/FullPageLoading";
import { useUserStore } from "@domains/auth/utils/useUserStore";
import { CreateBankAccountLinkSheet } from "./sheets/CreateBankAccountLinkSheet";
import BankAccountItem from "@domains/settings/components/BankAccountCard";
import { Body, Heading } from "@hypercard/web-components";
import { Bank } from "@phosphor-icons/react";
import { NoResultsCard } from "@common/components";
import { useApolloClient } from "@apollo/client";
import { isEligibleToAddBeneficiary } from "@common/state/organizationState";
import { useRecoilValue } from "recoil";

export const BankAccountTab = () => {
  const isEligibleToLinkAccount = useRecoilValue(isEligibleToAddBeneficiary);

  return (
    <BankAccountOnboardingState
      isEligibleToLinkAccount={isEligibleToLinkAccount}
    />
  );
};

const BankAccountOnboardingState = ({
  isEligibleToLinkAccount,
}: {
  isEligibleToLinkAccount: boolean;
}) => {
  const client = useApolloClient();
  const userStore = useUserStore.getState();
  const [readyToOnboard, setReadyToOnboard] = useState<boolean>(false);

  const { data: beneficiaryData, loading } = useBeneficiaryQuery();
  const [
    deleteBeneficiaryMutation,
    { data: deletingBeneficiaryData, loading: isDeleting },
  ] = useDeleteBeneficiaryMutation();

  const onClose = () => {
    setReadyToOnboard(false);
  };

  const handleOnboardingFlow = () => {
    setReadyToOnboard(true);
  };

  const beneficiaryAccount =
    beneficiaryData?.beneficiary?.__typename === "QueryBeneficiarySuccess"
      ? beneficiaryData.beneficiary.data.beneficiary.bankDetails
      : null;

  useEffect(() => {
    if (
      deletingBeneficiaryData?.deleteAwxBeneficiaryAccount.__typename ===
      "MutationDeleteAwxBeneficiaryAccountSuccess"
    ) {
      userStore.setIsBankAccountLinked(false);
      client.writeQuery({
        query: BeneficiaryDocument,
        data: {
          beneficiary: null,
        },
      });
    }
  }, [deletingBeneficiaryData, isDeleting, userStore, client]);

  if (loading && !beneficiaryAccount) return <FullPageLoader />;

  // 1. Check to see if bank account is able to be linked
  // 2. If bank account is already linked, show update button
  // 3. If bank account is not linked , show start

  if (!isEligibleToLinkAccount) {
    return (
      <NoResultsCard
        Icon={Bank}
        body={`Your organization is not able to connect to the bank account at this time.\nPlease contact your administrator for assistance.`}
        header="Unable to Connect"
      />
    );
  }

  const isAccountLinked = userStore.isBankAccountLinked && beneficiaryAccount;

  let subtitle: string = "";
  if (beneficiaryAccount) {
    const isUS = beneficiaryAccount?.bankCountryCode === "US";
    const displayLocalClearingSystem = beneficiaryAccount?.localClearingSystem
      ? ` · ${beneficiaryAccount.localClearingSystem}`
      : "";
    const displayAccountNumber = beneficiaryAccount.accountNumberLastFour
      ? ` · Account ****${beneficiaryAccount.accountNumberLastFour}`
      : "";
    /* For US, we don't bother showing the bank country code */
    subtitle = !isUS
      ? `${beneficiaryAccount.bankCountryCode}${displayLocalClearingSystem}${displayAccountNumber}`
      : `${displayLocalClearingSystem}${displayAccountNumber}`;
    subtitle = subtitle.startsWith(" · ") ? subtitle.slice(3) : subtitle;
  }
  if (!readyToOnboard) {
    return (
      <div>
        <div className="mt-4 flex flex-grow flex-col">
          <div className="min-w-screen flex flex-col">
            <div className="min-w-screen flex flex-row justify-between">
              <div className="start-0 flex flex-col items-start p-4">
                <Heading size="lg" weight="regular" className="mb-3 font-sans">
                  Personal Bank Account
                </Heading>
                <Body
                  size="sm"
                  weight="regular"
                  className="mr-5 font-sans text-base text-slate"
                >
                  {isAccountLinked
                    ? "All approved reimbursements will appear in your personal bank account in around 2-3 business days."
                    : "Connect your personal bank account to receive reimbursements from your company. Once your account is linked, all previously approved reimbursements will appear in your account within 2-3 business days."}
                </Body>
              </div>
              <div>
                <Button
                  className="mb-10 mt-8 w-[200px]"
                  onClick={handleOnboardingFlow}
                >
                  {isAccountLinked
                    ? "Update Personal Account"
                    : "Add Personal Account"}
                </Button>
              </div>
            </div>
            {isAccountLinked && (
              <div className="start-0 flex flex-col items-start p-4">
                <ul className="flex w-full flex-col gap-3">
                  <BankAccountItem
                    key={beneficiaryAccount.accountNumberLastFour || "Unknown"}
                    subtitle={subtitle}
                    accountName={beneficiaryAccount.accountName}
                    isDeleting={isDeleting}
                    onDelete={async () => await deleteBeneficiaryMutation()}
                  />
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (readyToOnboard) {
    return (
      <>
        <CreateBankAccountLinkSheet isOpen={readyToOnboard} onClose={onClose} />
      </>
    );
  }
};
